<template>
  <div class="body-wrapper">
    <div class="main-holder">
      <div class="cover-view">
        <img :src="getCoverPhotoUrl()" alt="Cover Photo"/>
      </div>

      <div class="audio-controls">
        <div class="audio-header">
          <span class="audio-title">{{questionData?.name}}</span>
          <span class="audio-description">Audio Attachment</span>
        </div>

        <div class="slider-container">
          <input
              type="range"
              class="slider"
              min="0"
              :max="duration"
              :value="currentTime"
              @input="seekAudio"
              :disabled="!audioLoaded"
          />
          <div class="slider-duration">
            <span class="duration-text">{{ formatTime(currentTime) }}</span>
            <span class="duration-text">{{ formatTime(duration) }}</span>
          </div>
        </div>

        <div class="play-button" @click="togglePlayPause" :class="{ 'disabled': !audioLoaded }">
          <img
              v-if="!isPlaying"
              src="@/assets/images/playWhite.svg"
              width="24"
              height="24"
              alt="Play Button"
          />
          <img
              v-else
              src="@/assets/images/pauseFill.svg"
              width="24"
              height="24"
              alt="Pause Button"
          />
        </div>
      </div>
    </div>

    <!-- Hidden audio element -->
    <audio
        ref="audioPlayer"
        :src="getAudioUrl()"
        @loadedmetadata="onAudioLoaded"
        @timeupdate="onTimeUpdate"
        @ended="onAudioEnded"
        @error="onAudioError"
        preload="metadata"
    ></audio>

    <img
        class="cover-photo"
        v-if="getCoverPhotoUrl() !== null"
        :src="getCoverPhotoUrl()"
        alt="Audio Attachment"
    />
    <div class="black-overlay" v-if="getCoverPhotoUrl() !== null"></div>
  </div>
</template>

<script>
export default{
  props:{
    questionData: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      audioLoaded: false
    }
  },

  mounted() {
    // Initialize audio when component mounts
    this.initializeAudio()
  },

  beforeDestroy() {
    // Clean up audio when component is destroyed
    if (this.$refs.audioPlayer) {
      this.$refs.audioPlayer.pause()
      this.$refs.audioPlayer.currentTime = 0
    }
  },

  watch: {
    questionData: {
      handler() {
        this.initializeAudio()
      },
      deep: true
    }
  },

  methods: {
    getAudioUrl() {
      const attachments = this.questionData?.content?.attach;

      if (!attachments || !Array.isArray(attachments)) {
        return null;
      }

      const audioAttachment = attachments.find(item =>
          item.type === 'audio' &&
          item.file &&
          item.file.attachUrl
      );

      return audioAttachment ? audioAttachment.file.attachUrl : null;
    },

    getCoverPhotoUrl() {
      const attachments = this.questionData?.content?.attach;

      if (!attachments || !Array.isArray(attachments)) {
        return null;
      }

      const coverAttachment = attachments.find(item =>
          item.type === 'cover' &&
          item.file &&
          item.file.attachUrl
      );

      return coverAttachment ? coverAttachment.file.attachUrl : null;
    },

    initializeAudio() {
      this.resetAudioState()
      this.$nextTick(() => {
        if (this.$refs.audioPlayer && this.getAudioUrl()) {
          this.$refs.audioPlayer.load()
        }
      })
    },

    resetAudioState() {
      this.isPlaying = false
      this.currentTime = 0
      this.duration = 0
      this.audioLoaded = false
    },

    togglePlayPause() {
      if (!this.audioLoaded || !this.$refs.audioPlayer) return

      if (this.isPlaying) {
        this.$refs.audioPlayer.pause()
      } else {
        this.$refs.audioPlayer.play().catch(error => {
          console.error('Error playing audio:', error)
        })
      }
    },

    seekAudio(event) {
      if (!this.audioLoaded || !this.$refs.audioPlayer) return

      const seekTime = parseFloat(event.target.value)
      this.$refs.audioPlayer.currentTime = seekTime
      this.currentTime = seekTime
    },

    onAudioLoaded() {
      if (this.$refs.audioPlayer) {
        this.duration = this.$refs.audioPlayer.duration || 0
        this.audioLoaded = true
        console.log('Audio loaded successfully, duration:', this.duration)
      }
    },

    onTimeUpdate() {
      if (this.$refs.audioPlayer) {
        this.currentTime = this.$refs.audioPlayer.currentTime
        this.isPlaying = !this.$refs.audioPlayer.paused
      }
    },

    onAudioEnded() {
      this.isPlaying = false
      this.currentTime = 0
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.currentTime = 0
      }
    },

    onAudioError(event) {
      console.error('Audio error:', event)
      this.resetAudioState()
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'

      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.floor(seconds % 60)
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.body-wrapper {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  height: 100%;
}

.cover-photo {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 0;
  object-fit: cover;
  top: 0;
  left: 0;
}

.black-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.75);
  z-index: 0;
  top: 0;
  left: 0;
}

.main-holder {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
}

.cover-view {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.cover-view img {
  width: 140px;
  height: 140px;
  border-radius: 16px;
}

.audio-controls {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
  width: 100%;
  gap: 10px;
}

.audio-header {
  gap: 3px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}

.audio-title {
  font-size: 20px;
  line-height: 20px;
  text-align: center;
  font-family: euclid_medium, sans-serif;
  color: white;
}

.audio-description {
  font-size: 14px;
  line-height: 14px;
  font-family: euclid_regular, sans-serif;
  color: #B3B3B7;
}

.slider-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 80%;
  gap: 10px;
}

.slider {
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 5px;
  background: #B3B3B7;
  outline: none;
  transition: opacity .2s;
  margin-top: 25px;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #FFFFFF;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #FFFFFF;
  cursor: pointer;
  border: none;
}

.slider:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.slider:disabled::-webkit-slider-thumb {
  cursor: not-allowed;
}

.slider-duration {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
}

.duration-text {
  color: white;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
}

.play-button {
  background-color: rgba(255,255,255,0.25);
  border-radius: 50%;
  height: 64px;
  width: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.play-button:hover {
  background-color: rgba(255,255,255,0.35);
}

.play-button:active {
  transform: scale(0.95);
}

.play-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.play-button.disabled:hover {
  background-color: rgba(255,255,255,0.25);
  transform: none;
}
</style>